import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, ListTodo, Calendar, User, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useTask, useUpdateTask } from '@/hooks/use-api'
import { toDisplayDate } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { explainError } from '@/lib/explain-error'

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: task, isLoading, error } = useTask(id!)
  useRecordBreadcrumb(task?.subject)
  const updateMutation = useUpdateTask()
  const { toast } = useToast()
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false)

  const handleMarkCompleted = async () => {
    try {
      await updateMutation.mutateAsync({ id: id!, data: { status: 'Completed' } })
      toast({
        title: 'Success',
        description: 'Task marked as completed.',
      })
      setFollowUpModalOpen(true)
    } catch (err) {
      const errorMessage = explainError(err, 'Failed to update task')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading task details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading task details...</p>
        </div>
      </div>
    )
  }

  if (error || !task) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading task</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Task not found')}
        </p>
        <Button
          onClick={() => navigate('/tasks')}
          className="mt-4"
          aria-label="Return to tasks list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Tasks
        </Button>
      </div>
    )
  }

  const formatDate = (date?: string) => {
    const d = toDisplayDate(date)
    if (!d) return 'N/A'
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusBadgeVariant = (status: string) => {
    const lowerStatus = status?.toLowerCase() || ''
    if (lowerStatus === 'completed') return 'success'
    if (lowerStatus === 'in progress') return 'warning'
    if (lowerStatus === 'not started') return 'secondary'
    return 'default'
  }

  const getPriorityBadgeVariant = (priority?: string) => {
    const lowerPriority = priority?.toLowerCase() || ''
    if (lowerPriority === 'high') return 'destructive'
    if (lowerPriority === 'normal') return 'default'
    if (lowerPriority === 'low') return 'secondary'
    return 'default'
  }

  return (
    <div className="space-y-6" role="main" aria-label="Task details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tasks')}
            aria-label="Back to tasks list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="task" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{task.subject || 'Untitled Task'}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {task.related_to_type && `Related to ${task.related_to_type}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusBadgeVariant(task.status)}>{task.status}</Badge>
          {task.priority && (
            <Badge variant={getPriorityBadgeVariant(task.priority)}>{task.priority}</Badge>
          )}
          {task.status !== 'Completed' && (
            <Button
              size="sm"
              onClick={handleMarkCompleted}
              disabled={updateMutation.isPending}
              aria-label="Mark task as completed"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
              {updateMutation.isPending ? 'Updating...' : 'Mark Completed'}
            </Button>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <ListTodo className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              <Badge variant={getStatusBadgeVariant(task.status)}>{task.status}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Due Date</CardTitle>
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium">
              {formatDate(task.due_date || task.activity_date)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Priority</CardTitle>
            <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium">
              {task.priority ? (
                <Badge variant={getPriorityBadgeVariant(task.priority)}>{task.priority}</Badge>
              ) : (
                'N/A'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Owner</CardTitle>
            <User className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium truncate">{task.owner_name || 'Unassigned'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Information */}
        <Card>
          <CardHeader>
            <CardTitle>Task Information</CardTitle>
            <CardDescription>Task details and scheduling</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Subject</Label>
                <div className="text-sm">{task.subject || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                <div className="text-sm">{task.status || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Priority</Label>
                <div className="text-sm">{task.priority || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Due Date</Label>
                <div className="text-sm">{formatDate(task.due_date || task.activity_date)}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Reminder Date</Label>
                <div className="text-sm">{formatDate(task.reminder_date)}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Completed Date</Label>
                <div className="text-sm">{formatDate(task.completed_date)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Related Information */}
        <Card>
          <CardHeader>
            <CardTitle>Related Information</CardTitle>
            <CardDescription>Related records and assignment</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Contact / Lead</Label>
                <div className="text-sm">
                  {task.who_id && task.who_name ? (
                    <Link
                      to={`/${task.who_type === 'Lead' ? 'leads' : 'contacts'}/${task.who_id}`}
                      className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      aria-label={`View ${task.who_type === 'Lead' ? 'lead' : 'contact'}: ${task.who_name}`}
                    >
                      {task.who_name}
                    </Link>
                  ) : (
                    'None'
                  )}
                </div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Related To</Label>
                <div className="text-sm">
                  {task.what_id && task.what_name ? (
                    <Link
                      to={`/${task.what_type === 'Account' ? 'accounts' : task.what_type === 'Opportunity' ? 'opportunities' : 'accounts'}/${task.what_id}`}
                      className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      aria-label={`View ${task.what_type || 'record'}: ${task.what_name}`}
                    >
                      {task.what_name}
                    </Link>
                  ) : (
                    task.related_to_type || 'N/A'
                  )}
                </div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Owner</Label>
                <div className="text-sm">{task.owner_name || 'Unassigned'}</div>
              </div>
              <div className="md:col-span-2 py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                <div className="text-sm whitespace-pre-wrap">{task.description || 'N/A'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attachments — documents linked to this task via the polymorphic
            content system (system.content_document_links). */}
        <section aria-label="Task attachments" className="space-y-3">
          <h2 className="text-lg font-semibold tracking-tight">Attachments</h2>
          <AttachmentsTab entityId={id!} entityType="Task" />
        </section>
      </div>

      <CreateTaskModal
        open={followUpModalOpen}
        onOpenChange={setFollowUpModalOpen}
        defaultSubject={`Follow-up: ${task.subject}`}
        relatedToId={task.what_id || task.who_id}
        relatedToType={task.what_type || task.who_type}
        relatedToName={task.what_name || task.who_name}
        ownerId={task.owner_id}
        defaultContacts={[
          ...(task.who_id && task.who_name
            ? [
                {
                  id: task.who_id,
                  name: task.who_name,
                  type: (task.who_id.startsWith('00Q') ? 'Lead' : 'Contact') as 'Contact' | 'Lead',
                },
              ]
            : []),
          ...(task.related_contacts?.map((rc) => ({
            id: rc.relation_id,
            name: rc.relation_name,
            type: rc.relation_type as 'Contact' | 'Lead',
          })) || []),
        ]}
      />
    </div>
  )
}
